/**
 * Right-click and drag for mosquitoes, on whichever scene the page draws.
 *
 * Worked out from the DOM rather than from each scene's geometry: a mosquito carries
 * `data-bug`, a person's station `data-drop-developer`, the jar `data-drop-jar`. Whatever is under
 * the pointer when it is let go is where the bug goes — the jar sends it back to waiting, a person
 * takes it on. The room, the Bug Hunt and the bug list all get the same behaviour from the same
 * attributes.
 *
 * Handled in the capture phase on the page's wrapper, so a press on a mosquito never starts the
 * room's own pan; a press that does not move is left alone and stays a click.
 */

import { useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react'

export type BugDrop = { kind: 'jar' } | { kind: 'developer'; id: string }

export interface BugMenuRequest {
  taskId: string
  x: number
  y: number
}

const THRESHOLD = 6

function dropUnder(x: number, y: number): { target: BugDrop; element: Element } | null {
  for (const element of document.elementsFromPoint(x, y)) {
    const developer = element.closest('[data-drop-developer]')
    if (developer) return { target: { kind: 'developer', id: developer.getAttribute('data-drop-developer')! }, element: developer }
    const jar = element.closest('[data-drop-jar]')
    if (jar) return { target: { kind: 'jar' }, element: jar }
  }

  // Nothing painted under the pointer — the gap between a person's card and their desk, say. The
  // smallest target whose box holds the pointer wins, so a drop near somebody counts as on them.
  let best: { target: BugDrop; element: Element; area: number } | null = null
  for (const element of document.querySelectorAll('[data-drop-developer], [data-drop-jar]')) {
    const box = element.getBoundingClientRect()
    if (x < box.left || x > box.right || y < box.top || y > box.bottom) continue
    const area = box.width * box.height
    if (best && best.area <= area) continue
    const id = element.getAttribute('data-drop-developer')
    best = { target: id ? { kind: 'developer', id } : { kind: 'jar' }, element, area }
  }
  return best ? { target: best.target, element: best.element } : null
}

export function useBugInteractions({
  enabled,
  onMenu,
  onDrop,
}: {
  enabled: boolean
  onMenu: (request: BugMenuRequest) => void
  onDrop: (taskId: string, target: BugDrop) => void
}) {
  const pending = useRef<{ taskId: string; startX: number; startY: number; dragging: boolean } | null>(null)
  const highlighted = useRef<Element | null>(null)
  const [ghost, setGhost] = useState<{ taskId: string; x: number; y: number; over: BugDrop | null } | null>(null)

  const onDropRef = useRef(onDrop)
  onDropRef.current = onDrop

  const highlight = (element: Element | null) => {
    if (highlighted.current === element) return
    highlighted.current?.classList.remove('bh-drop-target')
    element?.classList.add('bh-drop-target')
    highlighted.current = element
  }

  useEffect(() => {
    const move = (event: PointerEvent) => {
      const current = pending.current
      if (!current) return
      if (!current.dragging && Math.hypot(event.clientX - current.startX, event.clientY - current.startY) < THRESHOLD) return
      current.dragging = true
      const under = dropUnder(event.clientX, event.clientY)
      highlight(under?.element ?? null)
      setGhost({ taskId: current.taskId, x: event.clientX, y: event.clientY, over: under?.target ?? null })
    }

    const up = (event: PointerEvent) => {
      const current = pending.current
      pending.current = null
      if (!current) return
      highlight(null)
      setGhost(null)
      if (!current.dragging) return

      // The click that ends a drag would select whatever is under it; swallow that one click.
      const swallow = (click: MouseEvent) => {
        click.stopPropagation()
        click.preventDefault()
      }
      window.addEventListener('click', swallow, { capture: true, once: true })
      window.setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 50)

      const under = dropUnder(event.clientX, event.clientY)
      if (under) onDropRef.current(current.taskId, under.target)
    }

    const cancel = () => {
      pending.current = null
      highlight(null)
      setGhost(null)
    }

    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', cancel)
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', cancel)
    }
  }, [])

  const onPointerDownCapture = useCallback(
    (event: ReactPointerEvent) => {
      if (!enabled || event.button !== 0) return
      const bug = (event.target as Element).closest('[data-bug]')
      if (!bug) return
      pending.current = { taskId: bug.getAttribute('data-bug')!, startX: event.clientX, startY: event.clientY, dragging: false }
      // The room pans on a press anywhere that is not a person; a press on a mosquito is ours.
      event.stopPropagation()
    },
    [enabled],
  )

  const onContextMenu = useCallback(
    (event: ReactMouseEvent) => {
      if (!enabled) return
      const bug = (event.target as Element).closest('[data-bug]')
      if (!bug) return
      event.preventDefault()
      onMenu({ taskId: bug.getAttribute('data-bug')!, x: event.clientX, y: event.clientY })
    },
    [enabled, onMenu],
  )

  return { handlers: { onPointerDownCapture, onContextMenu }, ghost }
}
